/**
 * Headless layout solver for the OBSIDIAN React/SVG renderer.
 *
 * Cytoscape can't draw the OBSIDIAN node language (collar textPath, octagons,
 * map-pins, hexagons, bowed edges), but its layout engine is excellent — so we
 * keep it purely as a position solver: build a `cytoscape({ headless: true })`
 * instance with the SAME layout configs the production Cytoscape renderer uses,
 * run the layout synchronously, read `node.position()`, and hand x/y back to the
 * SVG renderer which draws everything itself.
 *
 * Post-processing: geo / mitre "property" nodes are pulled OUT of the dagre rank
 * assignment (they'd otherwise create spurious ranks) and pinned right next to
 * their single parent, offset along the parent→property vector.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import cytoscape from 'cytoscape';
import dagre from 'cytoscape-dagre';
import type { GraphElementDefinition } from '../types';
import { getLayoutConfig, type LayoutType, type Orientation } from './layoutConfigs';
import { getLayerForType, type GraphLayer } from '../categoryColors';

let dagreRegistered = false;

/** Register the dagre layout extension on the cytoscape factory once. */
function ensureDagre(): void {
  if (dagreRegistered) return;
  try {
    (cytoscape as any).use(dagre);
    dagreRegistered = true;
  } catch {
    // dagre unavailable — attack-path will fall back to a grid layout below.
  }
}

export interface LayoutPosition {
  x: number;
  y: number;
}

const PROPERTY_TYPES = new Set(['geolocation', 'mitre_technique', 'geo', 'mitre']);

function isNode(el: GraphElementDefinition): boolean {
  return el.group === 'nodes' || (!el.group && !el.data.source);
}
function isEdge(el: GraphElementDefinition): boolean {
  return el.group === 'edges' || (!el.group && !!el.data.source);
}

/**
 * Run the requested layout headlessly and return a map of node id → {x,y}.
 * Property nodes (geo/mitre) are excluded from the solve and re-pinned next to
 * their parent afterward.
 *
 * @param elements Cytoscape-shaped element definitions (nodes + edges).
 * @param layout   'attack-path' (dagre) | 'tree' (breadthfirst) | 'force' (cose)
 * @param orientation 'vertical' | 'horizontal'
 */
export function runLayout(
  elements: GraphElementDefinition[],
  layout: LayoutType,
  orientation: Orientation = 'vertical',
): Map<string, LayoutPosition> {
  const positions = new Map<string, LayoutPosition>();
  if (elements.length === 0) return positions;

  if (layout === 'attack-path') ensureDagre();

  const allNodes = elements.filter(isNode);
  const allEdges = elements.filter(isEdge);

  // Identify property nodes + the parent each attaches to.
  const propertyParents = new Map<string, string>(); // propertyId -> parentId
  const propertyIds = new Set<string>();
  for (const n of allNodes) {
    const type = n.data.type as string | undefined;
    if (type && PROPERTY_TYPES.has(type) && n.data.id) {
      propertyIds.add(n.data.id as string);
    }
  }
  for (const e of allEdges) {
    const src = e.data.source as string;
    const tgt = e.data.target as string;
    if (propertyIds.has(tgt) && !propertyIds.has(src)) propertyParents.set(tgt, src);
    else if (propertyIds.has(src) && !propertyIds.has(tgt)) propertyParents.set(src, tgt);
  }

  // Solve layout over the non-property subgraph so properties don't distort ranks.
  const solveNodes = allNodes.filter((n) => !propertyIds.has(n.data.id as string));
  const solveNodeIds = new Set(solveNodes.map((n) => n.data.id as string));
  const solveEdges = allEdges.filter(
    (e) => solveNodeIds.has(e.data.source as string) && solveNodeIds.has(e.data.target as string),
  );

  // The layered layout bypasses Cytoscape's layout engine entirely. Rank Y by
  // 6-layer ontology (infrastructure → identity → application → data →
  // threat → decisions); within a rank, sort X by greedy barycenter of edges
  // to nodes above so connected nodes cluster. Property nodes get pinned to
  // their parents afterward (same pass the dagre path uses).
  if (layout === 'layered') {
    const layeredPositions = solveLayered(solveNodes, solveEdges, orientation);
    for (const [id, p] of layeredPositions) positions.set(id, p);
    pinPropertyNodes(positions, propertyParents, propertyIds, allNodes, allEdges, orientation);
    return positions;
  }

  // Solve the non-property subgraph headlessly, optionally forcing a dagre rankDir.
  const solveWith = (rankDir?: 'TB' | 'LR'): Map<string, LayoutPosition> => {
    const pos = new Map<string, LayoutPosition>();
    const cy = cytoscape({
      headless: true,
      styleEnabled: false,
      elements: [
        ...solveNodes.map((n) => ({ group: 'nodes' as const, data: { ...n.data } })),
        ...solveEdges.map((e) => ({ group: 'edges' as const, data: { ...e.data } })),
      ],
    });
    if (cy.nodes().length > 0) {
      const cfg = { ...getLayoutConfig(layout, { orientation }), ...(rankDir ? { rankDir } : {}), animate: false, fit: false };
      try {
        cy.layout(cfg).run();
      } catch {
        // If a layout extension is missing, fall back to a grid so we still
        // produce deterministic positions rather than stacking at the origin.
        cy.layout({ name: 'grid', animate: false, fit: false } as any).run();
      }
    }
    cy.nodes().forEach((n) => {
      const p = n.position();
      pos.set(n.id(), { x: p.x, y: p.y });
    });
    cy.destroy();
    return pos;
  };

  const bboxOf = (pos: Map<string, LayoutPosition>): { gw: number; gh: number } => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of pos.values()) {
      minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
    }
    if (!Number.isFinite(minX)) return { gw: 1, gh: 1 };
    return { gw: Math.max(1, maxX - minX), gh: Math.max(1, maxY - minY) };
  };

  // Auto-orient (attack-path/dagre only): choose the rankDir whose solved bounding
  // box's long axis matches the panel's long axis, so the graph fills the panel —
  // a vertical (tall) panel wants a TALL graph, a horizontal (wide) panel a WIDE
  // one. A "star" (one hub + many same-rank siblings) is wide under TB; flipping
  // it to LR makes it a tall column that fills a vertical panel. A chain is tall
  // under TB and stays a single line either way.
  let chosenDir: 'TB' | 'LR' = orientation === 'vertical' ? 'TB' : 'LR';
  if (layout === 'attack-path' && solveNodes.length > 1) {
    const tb = solveWith('TB');
    const { gw, gh } = bboxOf(tb);
    const HYST = 1.2; // leave near-square graphs on TB
    chosenDir = orientation === 'vertical'
      ? (gw > gh * HYST ? 'LR' : 'TB')   // flip a wide star into a tall column
      : (gh > gw * HYST ? 'LR' : 'TB');  // flip a tall chain into a wide line
    const solved = chosenDir === 'TB' ? tb : solveWith('LR');
    for (const [id, p] of solved) positions.set(id, p);
  } else {
    for (const [id, p] of solveWith()) positions.set(id, p);
  }

  // Centroid of the solved (non-property) nodes — the in-bounds anchor for any
  // orphan property node (one with no resolvable parent edge). Pinning orphans
  // at (0,0) stranded them far outside the solved cluster, inflating the
  // renderer's fit() bounding box and shrinking the real graph into a corner.
  let cxSum = 0, cySum = 0, solvedN = 0;
  for (const id of solveNodeIds) {
    const p = positions.get(id);
    if (p) { cxSum += p.x; cySum += p.y; solvedN++; }
  }
  const centroid = solvedN > 0 ? { x: cxSum / solvedN, y: cySum / solvedN } : { x: 0, y: 0 };

  // Pin each property node next to its parent, offset perpendicular to the flow
  // (TB flows down → fan sideways; LR flows right → fan downward).
  const OFFSET = 70;
  // Spread multiple properties of the same parent so they don't overlap.
  const perParentCount = new Map<string, number>();
  let orphanIdx = 0;
  for (const propId of propertyIds) {
    const parentId = propertyParents.get(propId);
    const parentPos = parentId ? positions.get(parentId) : undefined;
    if (parentPos) {
      const idx = perParentCount.get(parentId!) ?? 0;
      perParentCount.set(parentId!, idx + 1);
      const angle = (chosenDir === 'LR' ? -Math.PI / 2 : 0) + (idx - 0.5) * 0.5;
      positions.set(propId, {
        x: parentPos.x + Math.cos(angle) * OFFSET + OFFSET * 0.5,
        y: parentPos.y + Math.sin(angle) * OFFSET + OFFSET * 0.4,
      });
    } else {
      // Orphan property node — place it near the graph centroid with a small
      // per-orphan spread so it stays in-bounds and multiple orphans don't stack.
      const a = orphanIdx * 1.2;
      orphanIdx++;
      positions.set(propId, { x: centroid.x + Math.cos(a) * OFFSET, y: centroid.y + Math.sin(a) * OFFSET });
    }
  }

  return positions;
}

// ─── Layered layout: rank by 6-layer ontology ─────────────────────────────
//
// Y position is determined by the node's ontology layer (infrastructure at
// top, decisions at bottom — same vertical order the existing ontology
// renders). X position within a layer is greedy barycenter-driven: nodes
// connected to many nodes one rank up get pulled toward those neighbors'
// average X, then collisions resolved by sorting + spacing.
//
// Property nodes (geo/mitre) are still re-pinned to their parents by the
// outer property pass — this function returns base positions only.

const LAYER_ORDER: GraphLayer[] = [
  'infrastructure',
  'identity',
  'application',
  'data',
  'threat',
  'decisions',
];
const LAYERED_RANK_SEP = 160;
const LAYERED_NODE_SEP = 170;

function solveLayered(
  allNodes: GraphElementDefinition[],
  allEdges: GraphElementDefinition[],
  orientation: Orientation,
): Map<string, LayoutPosition> {
  const pos = new Map<string, LayoutPosition>();
  // The caller (runLayout) already passes the non-property `solveNodes`, so
  // no further filtering is required here. Kept as a one-line accessor to
  // make the rest of the function read symmetrically with `allEdges`.
  const nodes = allNodes;

  // 1. Assign each node to a layer.
  const layerOf = new Map<string, GraphLayer>();
  for (const n of nodes) {
    const t = (n.data.type as string) ?? '';
    layerOf.set(n.data.id as string, getLayerForType(t));
  }

  // 2. Group nodes by layer (in canonical layer order).
  const byLayer = new Map<GraphLayer, GraphElementDefinition[]>();
  for (const layer of LAYER_ORDER) byLayer.set(layer, []);
  for (const n of nodes) {
    const layer = layerOf.get(n.data.id as string)!;
    byLayer.get(layer)!.push(n);
  }

  // 3. Build a neighbor index for barycenter sorting (one-rank-up neighbors).
  const upNeighbors = new Map<string, string[]>();
  for (const e of allEdges) {
    const s = e.data.source as string;
    const t = e.data.target as string;
    const ls = layerOf.get(s);
    const lt = layerOf.get(t);
    if (!ls || !lt) continue;
    const li = LAYER_ORDER.indexOf(ls);
    const lj = LAYER_ORDER.indexOf(lt);
    // Map both directions of the edge to "the lower-rank endpoint pulls toward
    // the higher-rank endpoint" — the layered look puts an `ip` near the
    // `domain` that resolves to it whether the LLM emitted `domain → ip` or
    // `ip → domain`.
    if (li < lj) {
      const arr = upNeighbors.get(t) ?? [];
      arr.push(s);
      upNeighbors.set(t, arr);
    } else if (lj < li) {
      const arr = upNeighbors.get(s) ?? [];
      arr.push(t);
      upNeighbors.set(s, arr);
    }
  }

  // 4. Assign Y by layer; sort X within each layer by barycenter of
  //    same-or-up-rank neighbors already placed.
  //
  //    Skipped layers COLLAPSE — `layerIndex` only advances when a layer is
  //    populated. A graph of just `host` + `alert` (infrastructure + threat)
  //    renders as two adjacent rows, not as two rows with a 4-rank gap
  //    between them. Cross-graph layer-Y consistency would be nice but it
  //    produces ugly empty bands on common subsets of the ontology.
  const xByNode = new Map<string, number>();
  let layerIndex = 0;
  for (const layer of LAYER_ORDER) {
    const layerNodes = byLayer.get(layer)!;
    if (layerNodes.length === 0) continue; // no nodes → don't reserve a row

    const sortKey = (n: GraphElementDefinition): number => {
      const ups = upNeighbors.get(n.data.id as string) ?? [];
      let sum = 0, c = 0;
      for (const u of ups) {
        const x = xByNode.get(u);
        if (x !== undefined) { sum += x; c++; }
      }
      return c === 0
        // No placed neighbor — keep stable by id so the layout is deterministic.
        ? hashStr(n.data.id as string)
        : sum / c;
    };
    const ordered = [...layerNodes].sort((a, b) => sortKey(a) - sortKey(b));

    const y = layerIndex * LAYERED_RANK_SEP;
    const xStart = -((ordered.length - 1) * LAYERED_NODE_SEP) / 2;
    ordered.forEach((n, i) => {
      const x = xStart + i * LAYERED_NODE_SEP;
      xByNode.set(n.data.id as string, x);
      // For horizontal orientation, swap so rank flows left-to-right and within-
      // rank spreads vertically — same shape, rotated 90°.
      if (orientation === 'horizontal') {
        pos.set(n.data.id as string, { x: y, y: x });
      } else {
        pos.set(n.data.id as string, { x, y });
      }
    });
    layerIndex++;
  }

  return pos;
}

/** Stable 32-bit hash → numeric tiebreaker so the layered layout is
 *  deterministic across renders. */
function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Layered-layout property-pinning pass — same shape as the inline pass in
 * `runLayout` but factored out so the layered path can reuse it.
 */
function pinPropertyNodes(
  positions: Map<string, LayoutPosition>,
  propertyParents: Map<string, string>,
  propertyIds: Set<string>,
  _allNodes: GraphElementDefinition[],
  _allEdges: GraphElementDefinition[],
  orientation: Orientation,
): void {
  let cxSum = 0, cySum = 0, solvedN = 0;
  for (const p of positions.values()) { cxSum += p.x; cySum += p.y; solvedN++; }
  const centroid = solvedN > 0 ? { x: cxSum / solvedN, y: cySum / solvedN } : { x: 0, y: 0 };

  const OFFSET = 70;
  const perParentCount = new Map<string, number>();
  let orphanIdx = 0;
  for (const propId of propertyIds) {
    const parentId = propertyParents.get(propId);
    const parentPos = parentId ? positions.get(parentId) : undefined;
    if (parentPos) {
      const idx = perParentCount.get(parentId!) ?? 0;
      perParentCount.set(parentId!, idx + 1);
      const angle = (orientation === 'horizontal' ? -Math.PI / 2 : 0) + (idx - 0.5) * 0.5;
      positions.set(propId, {
        x: parentPos.x + Math.cos(angle) * OFFSET + OFFSET * 0.5,
        y: parentPos.y + Math.sin(angle) * OFFSET + OFFSET * 0.4,
      });
    } else {
      const a = orphanIdx * 1.2;
      orphanIdx++;
      positions.set(propId, { x: centroid.x + Math.cos(a) * OFFSET, y: centroid.y + Math.sin(a) * OFFSET });
    }
  }
}
